/**
 * First-run setup wizard (ADR-0073 dec. 4, post-boot half).
 *
 * An AUTHENTICATED, admin-gated first-run experience — NOT an anonymous mutation
 * surface. When the initial admin still holds the default password the shell shows
 * a dismissible BANNER; clicking it opens a guided DIALOG with three steps:
 *   1. rotate the default admin password (link to the profile page),
 *   2. paste the activation key (reuses `licenseApi.activate`, only when enforced),
 *   3. download the recovery config (vault key + DB URL).
 *
 * "Pending" is DERIVED server-side from the default password, so changing it
 * auto-dismisses the wizard on the next status refresh (self-latching, no storage).
 * Rendered near the top of `AppShell`; renders nothing when setup is done.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Circle, Download, KeyRound, Lock, X } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { type LicenseStatus, licenseApi } from "@/lib/api-license";
import { type RecoveryConfig, setupApi } from "@/lib/api-setup";
import { cn } from "@/lib/cn";
import { usePermissions } from "@/lib/permissions";

const DISMISS_KEY = "tray:setup:dismissed";

function StepRow({
  done,
  title,
  children,
}: {
  done: boolean;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex gap-3">
      <div className="mt-0.5 shrink-0">
        {done ? (
          <CheckCircle2 className="h-5 w-5 text-success" strokeWidth={1.75} />
        ) : (
          <Circle className="h-5 w-5 text-muted-foreground" strokeWidth={1.5} />
        )}
      </div>
      <div className="min-w-0 flex-1 space-y-2">
        <h4 className={cn("text-sm font-semibold", done ? "text-muted-foreground" : "text-foreground")}>
          {title}
        </h4>
        {children}
      </div>
    </div>
  );
}

export function SetupWizard() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { can } = usePermissions();
  const canRead = can("settings", "read");
  const canWrite = can("settings", "write");

  // Only fetch when the operator can read settings — a scoped user never sees the wizard.
  const q = useQuery({
    queryKey: ["setup", "status"],
    queryFn: setupApi.status,
    enabled: canRead,
    retry: false,
  });

  const [open, setOpen] = React.useState(false);
  const [key, setKey] = React.useState("");
  const [dismissed, setDismissed] = React.useState<boolean>(
    () => typeof window !== "undefined" && window.localStorage.getItem(DISMISS_KEY) === "1",
  );

  const activate = useMutation({
    mutationFn: () => licenseApi.activate(key.trim()),
    onSuccess: (data: LicenseStatus) => {
      // The activation flips `licensed` — refresh the wizard status.
      qc.setQueryData(["license", "status"], data);
      void qc.invalidateQueries({ queryKey: ["setup", "status"] });
      setKey("");
    },
  });

  const recovery = useMutation({
    mutationFn: () => setupApi.recovery(),
    onSuccess: (cfg: RecoveryConfig) => downloadRecovery(cfg),
  });

  const status = q.data;
  if (!status || !status.pending || dismissed) return null;

  const dismiss = () => {
    setDismissed(true);
    setOpen(false);
    window.localStorage.setItem(DISMISS_KEY, "1");
  };

  const licenseStep = status.enforced; // only relevant on a licensed on-prem build

  return (
    <>
      {/* Banner — full-width, above the chrome. */}
      <div className="flex items-center gap-3 border-b border-warning/40 bg-warning/10 px-4 py-2 text-sm">
        <KeyRound className="h-4 w-4 shrink-0 text-warning" strokeWidth={1.75} />
        <span className="min-w-0 flex-1 text-foreground">
          {t(
            "setup.banner",
            "Configurazione iniziale da completare: cambia la password admin predefinita e salva la configurazione di ripristino.",
          )}
        </span>
        <Button size="sm" onClick={() => setOpen(true)}>
          {t("setup.open", "Completa")}
        </Button>
        <button
          onClick={dismiss}
          className="shrink-0 rounded p-1 text-muted-foreground hover:bg-muted"
          aria-label={t("setup.dismiss", "Nascondi")}
        >
          <X className="h-4 w-4" strokeWidth={1.5} />
        </button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-sm font-semibold">
              {t("setup.title", "Configurazione iniziale")}
            </DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p className="text-xs text-muted-foreground">
              {t(
                "setup.subtitle",
                "Alcuni passaggi proteggono la tua installazione. Puoi tornare qui in qualsiasi momento dalle Impostazioni.",
              )}
            </p>

            <StepRow
              done={!status.default_password}
              title={t("setup.step.password", "Cambia la password admin predefinita")}
            >
              <p className="text-xs text-muted-foreground">
                {t(
                  "setup.step.passwordHint",
                  "L'utente admin ({{user}}) usa ancora la password predefinita. Cambiala per chiudere questo passaggio.",
                  { user: status.admin_username },
                )}
              </p>
              {status.default_password && (
                <Button asChild size="sm" variant="outline" onClick={() => setOpen(false)}>
                  <Link to="/profile">{t("setup.step.passwordCta", "Vai al profilo")}</Link>
                </Button>
              )}
            </StepRow>

            {licenseStep && (
              <StepRow
                done={status.licensed}
                title={t("setup.step.license", "Attiva la licenza")}
              >
                {status.licensed ? (
                  <p className="text-xs text-muted-foreground">
                    {t("setup.step.licenseDone", "Licenza attiva.")}
                  </p>
                ) : (
                  <>
                    <p className="text-xs text-muted-foreground">
                      {t("setup.step.licenseHint", "Incolla la chiave di attivazione ricevuta.")}
                    </p>
                    <div className="flex items-start gap-2">
                      <Input
                        value={key}
                        onChange={(e) => setKey(e.target.value.toUpperCase())}
                        placeholder="XXXX-XXXX-XXXX-XXXX-XXXX"
                        className="font-mono"
                        disabled={!canWrite || activate.isPending}
                        spellCheck={false}
                      />
                      <Button
                        size="sm"
                        onClick={() => activate.mutate()}
                        disabled={!canWrite || !key.trim() || activate.isPending}
                      >
                        {activate.isPending
                          ? t("setup.step.licenseActivating", "Attivazione…")
                          : t("setup.step.licenseActivate", "Attiva")}
                      </Button>
                    </div>
                    {activate.isError && (
                      <p className="text-xs text-danger">
                        {t("setup.step.licenseError", "Attivazione non riuscita. Controlla la chiave.")}
                      </p>
                    )}
                  </>
                )}
              </StepRow>
            )}

            <StepRow done={false} title={t("setup.step.recovery", "Salva la configurazione di ripristino")}>
              <p className="text-xs text-muted-foreground">
                {t(
                  "setup.step.recoveryHint",
                  "Contiene la chiave del vault e l'URL del database: senza la chiave del vault i segreti sono irrecuperabili. Conservala in un posto sicuro.",
                )}
              </p>
              <Button
                size="sm"
                variant="outline"
                onClick={() => recovery.mutate()}
                disabled={!canWrite || recovery.isPending}
              >
                <Download className="mr-1.5 h-3.5 w-3.5" strokeWidth={1.75} />
                {recovery.isPending
                  ? t("setup.step.recoveryDownloading", "Preparazione…")
                  : t("setup.step.recoveryCta", "Scarica configurazione")}
              </Button>
              {recovery.isError && (
                <p className="text-xs text-danger">
                  {t("setup.step.recoveryError", "Download non riuscito.")}
                </p>
              )}
              {!canWrite && (
                <p className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Lock className="h-3 w-3" strokeWidth={1.75} />
                  {t("setup.needWrite", "Serve il permesso settings:write.")}
                </p>
              )}
            </StepRow>
          </DialogBody>
        </DialogContent>
      </Dialog>
    </>
  );
}

/** Serialize the recovery bundle to a downloadable `.env`-style file. */
function downloadRecovery(cfg: RecoveryConfig) {
  const body =
    "# Tray recovery configuration — keep this secret and backed up.\n" +
    "# Losing HUB_VAULT_MASTER_KEY makes every stored credential unrecoverable.\n" +
    `HUB_VAULT_MASTER_KEY=${cfg.vault_key}\n` +
    `DATABASE_URL=${cfg.database_url}\n`;
  const blob = new Blob([body], { type: "text/plain" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "tray-recovery.env";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
